/**
 * TenderScenarioAnalysis — Tab 8: Scenario builder + results + comparison.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Play, Wand2, Trash2, GitCompare, AlertTriangle, Brain, Loader2,
} from 'lucide-react';
import api from '@/lib/api';
import ResourceLoadingChart from './ResourceLoadingChart';

const fmtINR = (v) => {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v || 0);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(1)} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  return `₹${(v || 0).toFixed(0)}`;
};

const OUTCOMES = ['WIN', 'LOSE', 'PROBABILITY'];

const TenderScenarioAnalysis = ({ tenders = [] }) => {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);
  const [compareSel, setCompareSel] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [newName, setNewName] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/tenders/scenarios');
      setScenarios(r.data?.scenarios || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const handleNew = async () => {
    const name = newName.trim() || `Scenario ${scenarios.length + 1}`;
    const assumptions = tenders.map((t) => ({
      tenderId: t.id || t.tender_id,
      tenderNumber: t.tender_reference || '',
      tenderName: t.title || '',
      client: t.client_name || '',
      originalValue: Number(t.estimated_value || 0),
      originalWinProb: Number(t.win_probability || 0),
      assumedOutcome: 'PROBABILITY',
    }));
    try {
      const r = await api.post('/tenders/scenarios', {
        scenarioName: name,
        description: 'User-defined scenario',
        tenderAssumptions: assumptions,
      });
      setActive(r.data);
      setNewName('');
      await load();
      toast.success(`Created "${name}"`);
    } catch (err) {
      toast.error('Create failed');
    }
  };

  const handleAutoGenerate = async () => {
    try {
      const r = await api.post('/tenders/scenarios/auto-generate');
      toast.success(`Generated ${r.data.created} scenarios — analysing in background`);
      setTimeout(load, 12000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-generate failed');
    }
  };

  const handleRun = async () => {
    if (!active?.id) return;
    setRunning(true);
    try {
      const start = await api.post(`/tenders/scenarios/${active.id}/analyze`);
      const jobId = start.data?.jobId;
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/tenders/scenarios/analyze/status/${jobId}`);
        if (poll.data?.status === 'done') break;
        if (poll.data?.status === 'failed') {
          throw new Error(poll.data?.error || 'Analysis failed');
        }
      }
      const r = await api.get(`/tenders/scenarios/${active.id}`);
      setActive(r.data);
      await load();
      toast.success('Scenario analysed');
    } catch (err) {
      toast.error(err.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tenders/scenarios/${id}`);
      if (active?.id === id) setActive(null);
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const handleAssumptionChange = (idx, field, value) => {
    if (!active) return;
    const next = [...(active.tenderAssumptions || [])];
    next[idx] = { ...next[idx], [field]: value };
    const updated = { ...active, tenderAssumptions: next };
    setActive(updated);
    // Auto-persist
    api.put(`/tenders/scenarios/${active.id}`, { tenderAssumptions: next })
       .catch(() => toast.error('Save failed'));
  };

  const handleCompare = async () => {
    if (compareSel.length < 2) return toast.error('Select 2-3 scenarios to compare');
    try {
      const r = await api.post('/tenders/scenarios/compare', { scenarioIds: compareSel });
      setComparison(r.data);
    } catch { toast.error('Compare failed'); }
  };

  const results = active?.results || {};
  const fin = results.financials || {};
  const conc = results.concentrationRisk || {};

  return (
    <div className="space-y-4" data-testid="tender-scenario-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="New scenario name…"
                className="w-64 bg-[#1F2937] border-[#3A3F4A] text-white h-9 text-sm"
                data-testid="new-scenario-input" />
        <Button onClick={handleNew} size="sm" className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]"
                disabled={!tenders.length} data-testid="create-scenario-btn">
          <Plus className="w-3 h-3 mr-1" />New
        </Button>
        <Button onClick={handleAutoGenerate} size="sm" variant="outline"
                className="border-[#6B5CE7] text-[#6B5CE7]" data-testid="auto-gen-btn">
          <Wand2 className="w-3 h-3 mr-1" />Auto-Generate 3
        </Button>
        <div className="flex-1" />
        {scenarios.length > 0 && (
          <Select value={active?.id || ''} onValueChange={async (id) => {
            const r = await api.get(`/tenders/scenarios/${id}`); setActive(r.data);
          }}>
            <SelectTrigger className="w-72 h-9 bg-[#1F2937] border-[#3A3F4A] text-white text-sm" data-testid="scenario-picker">
              <SelectValue placeholder="Select a scenario…" />
            </SelectTrigger>
            <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
              {scenarios.map((s) => (
                <SelectItem key={s.id} value={s.id} className="text-slate-200">
                  {s.scenarioName} — {s.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Scenario builder */}
      {active && (
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">{active.scenarioName}</h3>
                <p className="text-[10px] text-slate-500">{active.scenarioCode} · {active.status}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => handleDelete(active.id)}
                        className="text-red-400 hover:bg-red-500/10" data-testid="delete-scenario-btn">
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
                <Button size="sm" onClick={handleRun} disabled={running}
                        className="bg-gradient-to-r from-emerald-600 to-emerald-500"
                        data-testid="run-scenario-btn">
                  {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Run Analysis
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="assumptions-table">
                <thead className="text-slate-400 border-b border-[#3A3F4A]">
                  <tr>
                    <th className="text-left p-2">Tender</th>
                    <th className="text-left p-2">Client</th>
                    <th className="text-right p-2">Value</th>
                    <th className="text-right p-2">Curr Win%</th>
                    <th className="text-left p-2">Assume</th>
                    <th className="text-right p-2">Override Prob%</th>
                    <th className="text-right p-2">Override Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(active.tenderAssumptions || []).map((a, idx) => (
                    <tr key={a.tenderId} className="border-b border-[#3A3F4A]/40">
                      <td className="p-2 font-mono text-slate-200">{a.tenderNumber || '—'}</td>
                      <td className="p-2 text-slate-400 truncate max-w-[150px]">{a.client || '—'}</td>
                      <td className="p-2 text-right text-slate-300">{fmtINR(a.originalValue)}</td>
                      <td className="p-2 text-right text-slate-300">{(a.originalWinProb || 0).toFixed(0)}%</td>
                      <td className="p-2">
                        <Select value={a.assumedOutcome || 'PROBABILITY'}
                                onValueChange={(v) => handleAssumptionChange(idx, 'assumedOutcome', v)}>
                          <SelectTrigger className="h-7 w-32 bg-[#111827] border-[#3A3F4A] text-white text-xs"
                                          data-testid={`outcome-select-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                            {OUTCOMES.map((o) => (
                              <SelectItem key={o} value={o} className="text-slate-200">{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2">
                        <Input type="number" value={a.overrideWinProb ?? ''}
                               disabled={a.assumedOutcome !== 'PROBABILITY'}
                               onChange={(e) => handleAssumptionChange(idx, 'overrideWinProb',
                                   e.target.value === '' ? null : Number(e.target.value))}
                               placeholder="—"
                               className="h-7 w-20 ml-auto bg-[#111827] border-[#3A3F4A] text-white text-xs text-right" />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={a.overrideContractValue ?? ''}
                               onChange={(e) => handleAssumptionChange(idx, 'overrideContractValue',
                                   e.target.value === '' ? null : Number(e.target.value))}
                               placeholder="—"
                               className="h-7 w-32 ml-auto bg-[#111827] border-[#3A3F4A] text-white text-xs text-right" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {active?.results && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-[#1F2937] border-emerald-500/40 border">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase">Total Revenue</p>
                <p className="text-lg font-bold text-emerald-400 mt-1">{fmtINR(fin.totalRevenue)}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase">Blended Margin</p>
                <p className="text-lg font-bold text-white mt-1">{(fin.blendedMarginPct || 0).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1F2937] border-amber-500/40 border">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase">Peak Funding</p>
                <p className="text-lg font-bold text-amber-400 mt-1">{fmtINR(fin.peakFundingRequirement)}</p>
                <p className="text-[10px] text-slate-500">{fin.peakFundingMonth}</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase">Outcomes</p>
                <p className="text-lg font-bold text-white mt-1">{results.expectedWins} / {results.expectedLosses}</p>
                <p className="text-[10px] text-slate-500">Win / Lose</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1F2937] border-red-500/40 border">
              <CardContent className="p-3">
                <p className="text-[10px] text-slate-500 uppercase">HHI Concentration</p>
                <p className="text-lg font-bold text-red-400 mt-1">{(conc.herfindahlIndex || 0).toFixed(3)}</p>
                <p className="text-[10px] text-slate-500">Top client: {(conc.topClientPct || 0).toFixed(0)}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Resource loading */}
          {results.resourceDemand?.length > 0 && (
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Resource Loading (MHrs / month)</h3>
                <ResourceLoadingChart data={results.resourceDemand} />
              </CardContent>
            </Card>
          )}

          {/* Revenue by year */}
          {results.revenueByYear?.length > 0 && (
            <Card className="bg-[#1F2937] border-[#3A3F4A]">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Revenue by Year</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {results.revenueByYear.map((r) => (
                    <div key={r.year} className="p-3 rounded border border-[#3A3F4A] bg-slate-900/40">
                      <p className="text-[10px] text-slate-500 uppercase">{r.year}</p>
                      <p className="text-base font-bold text-white">{fmtINR(r.revenue)}</p>
                      <p className="text-[10px] text-emerald-400 mt-1">Profit: {fmtINR(r.profit)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Narrative */}
          {results.aiAnalysis?.narrative && (
            <Card className="bg-[#1F2937] border-[#6B5CE7]/40 border-2">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Brain className="w-4 h-4 text-[#6B5CE7]" />Cognara Analysis
                </h3>
                <p className="text-xs text-slate-300">{results.aiAnalysis.narrative}</p>
                {(results.aiAnalysis.keyRisks || []).length > 0 && (
                  <div>
                    <p className="text-amber-400 text-xs font-medium mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />Key Risks
                    </p>
                    <ul className="text-xs text-slate-300 list-disc list-inside">
                      {results.aiAnalysis.keyRisks.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {(results.aiAnalysis.recommendations || []).length > 0 && (
                  <div>
                    <p className="text-emerald-400 text-xs font-medium mb-1">Recommendations</p>
                    <ul className="text-xs text-slate-300 list-disc list-inside">
                      {results.aiAnalysis.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Comparison */}
      {scenarios.length >= 2 && (
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-emerald-400" />Compare Scenarios (2-3)
            </h3>
            <div className="flex flex-wrap gap-2">
              {scenarios.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-xs text-slate-300">
                  <Checkbox checked={compareSel.includes(s.id)}
                             onCheckedChange={(c) => {
                               if (c && compareSel.length < 3) setCompareSel([...compareSel, s.id]);
                               else if (!c) setCompareSel(compareSel.filter((id) => id !== s.id));
                             }}
                             data-testid={`compare-check-${s.id}`} />
                  {s.scenarioName}
                </label>
              ))}
              <Button size="sm" onClick={handleCompare} disabled={compareSel.length < 2}
                      className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7] ml-auto"
                      data-testid="run-compare-btn">
                Compare
              </Button>
            </div>

            {comparison?.comparison?.length > 0 && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr><th className="text-left p-2">Metric</th>
                      {comparison.comparison.map((c) => (
                        <th key={c.scenarioId} className="text-right p-2">{c.scenarioName}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {[
                      ['Total Revenue', 'totalRevenue', fmtINR],
                      ['Total Cost', 'totalCost', fmtINR],
                      ['Gross Profit', 'totalGrossProfit', fmtINR],
                      ['Blended Margin', 'blendedMarginPct', (v) => `${v.toFixed(1)}%`],
                      ['Peak Funding', 'peakFundingRequirement', fmtINR],
                      ['Expected Wins', 'expectedWins', (v) => v],
                      ['Expected Losses', 'expectedLosses', (v) => v],
                      ['Top Client %', 'topClientPct', (v) => `${v.toFixed(1)}%`],
                      ['HHI', 'concentrationHHI', (v) => v.toFixed(3)],
                    ].map(([label, key, f]) => (
                      <tr key={key} className="border-t border-[#3A3F4A]/40">
                        <td className="p-2 text-slate-400">{label}</td>
                        {comparison.comparison.map((c) => (
                          <td key={c.scenarioId} className="p-2 text-right font-mono">
                            {f(c[key] || 0)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenderScenarioAnalysis;
